import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useCommon } from "../../../context/MyContext";
import { useUIStore } from "../store";
import { useUserQuery } from "./queries/useUserQuery";
import { User } from "../../../api/userApi";
import { clearDeviceCheckCache } from "../../../utils/contentDeviceCache";

/**
 * AlertSetting 타입 정의 (MyContext에서 사용)
 */
interface AlertSetting {
  btnTxt: string;
  alertTxt: string;
  double: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
}

/**
 * HTTP 에러 타입
 */
interface AuthError {
  status: 401 | 403 | 404;
  detail?: string;
}

/**
 * 인증 관련 액션을 제공하는 커스텀 훅
 *
 * - 로그인/로그아웃 액션
 * - 구독/권한 검증
 * - HTTP 에러 처리
 * - 사용자 상태는 useUserQuery 훅을 별도로 사용
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // MyContext에서 필요한 값 가져오기
  const {
    setIsLogin,
    setAlertSetting,
    setUser: setContextUser,
    setHistory,
    setList,
    setStoredList,
  } = useCommon();

  // UIStore에서 referrerUrl 관리
  const { referrerUrl, setReferrerUrl } = useUIStore();

  // useUserQuery에서 resetAuth 및 user 가져오기
  const { resetAuth, user } = useUserQuery();

  /**
   * 로그아웃 처리
   * 1. API 호출
   * 2. 전역 상태 초기화 (Zustand + MyContext)
   * 3. 스토리지 정리
   * 4. 홈으로 리다이렉트
   */
  const logout = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_LOGOUT_URL}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        },
      );

      if (response.status === 200) {
        // 1. Zustand store 초기화
        resetAuth();

        // 2. MyContext 초기화
        const emptyUser: User = {
          id: null,
          name: null,
          tone: null,
          picture_extension: null,
          picture_data: null,
          paid: false,
          start_dt: null,
          end_dt: null,
          tutor_cnt: 0,
          socra_cnt: 0,
        };

        setContextUser(emptyUser);
        setHistory([]);
        setList([]);
        setStoredList([]);

        // 3. 스토리지 정리
        sessionStorage.removeItem("userInfo");
        sessionStorage.removeItem("item");
        localStorage.removeItem("userInfo");

        // 기기 판정 캐시 정리 (다음 로그인 계정이 이전 판정을 재사용하지 않도록)
        clearDeviceCheckCache();

        // 4. tanstack-query 캐시 무효화 (useUser 자동 갱신)
        queryClient.invalidateQueries({ queryKey: ["user"] });
        (window as any).Android?.logout();

        Cookies.set("accessToken", "", {
          expires: new Date(0),
          domain: ".dongascience.com",
          path: "/",
        });
        Cookies.set("refreshToken", "", {
          expires: new Date(0),
          domain: ".dongascience.com",
          path: "/",
        });

        Cookies.set("DS_SESSION", "", {
          expires: new Date(0),
          domain: ".dongascience.com",
          path: "/",
        });

        // 5. 홈으로 리다이렉트
        navigate("/");
      } else {
        // 로그아웃 실패 시 에러 알림
        setAlertSetting((prev: AlertSetting) => ({
          ...prev,
          alertTxt: "로그아웃에 실패했습니다. 다시 시도해주세요.",
          btnTxt: "닫기",
          double: false,
        }));
      }
    } catch (error) {
      console.error("Logout error:", error);
      setAlertSetting((prev: AlertSetting) => ({
        ...prev,
        alertTxt: "로그아웃 중 오류가 발생했습니다.",
        btnTxt: "닫기",
        double: false,
      }));
    }
  };

  // UIStore에서 로그아웃 확인 모달 상태
  const { logoutConfirmOpen, setLogoutConfirmOpen } = useUIStore();

  /**
   * 로그아웃 확인 다이얼로그 표시
   * ConfirmModal을 사용하여 확인 다이얼로그 표시
   */
  const showLogoutConfirm = () => {
    setLogoutConfirmOpen(true);
  };

  /* 로그인 상태 확인 후 액션 실행
   * @param action 로그인 상태일 때 실행할 함수
   * @param onValidate 비로그인 상태일 때 실행할 함수 (기본: redirectToLogin)
   */
  const checkLoginBeforeAction = <T extends unknown[]>(
    action: (...args: T) => void,
    onValidate: () => void = redirectToLogin,
  ) => {
    return (...args: T) => {
      queryClient.getQueryData(["user"]) ? action(...args) : onValidate?.();
    };
  };

  /**
   * SSO 로그인 페이지로 리다이렉트
   * MyContext의 referrerUrl을 확인하고, 없으면 디폴트 URL 사용
   * 현재 URL의 쿼리스트링을 그대로 유지하여 로그인 후 원래 URL로 복귀
   */
  const redirectToLogin = () => {
    // 현재 URL의 pathname + search 조합으로 전체 경로 구성
    const currentPath =
      window.location.pathname === "/" ? "/tutor" : window.location.pathname;
    const currentSearch = window.location.search;
    const fullPath = `${currentPath}${currentSearch}`;

    const refererUrl =
      referrerUrl || `${process.env.REACT_APP_REFERER_URL}${fullPath}`;

    // referer_url은 인코딩하지 않음 (SSO 서버에서 그대로 리다이렉트)
    const loginUrl = `${process.env.REACT_APP_LOGIN_URL}/?referer_url=${refererUrl}`;
    window.location.assign(loginUrl);
  };

  /**
   * 로그인 모달 표시
   * @param redirectUrl 로그인 후 돌아올 URL
   */
  const showLoginModal = (redirectUrl?: string) => {
    if (redirectUrl) {
      setReferrerUrl(redirectUrl);
    }
    setIsLogin(true);
  };

  return {
    // 기존 기능
    logout,
    showLogoutConfirm,
    logoutConfirmOpen,
    setLogoutConfirmOpen,
    redirectToLogin,
    showLoginModal,
    checkLoginBeforeAction,
  };
};
